import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, ChevronRight, Lightbulb, User, Heart, MapPin, Target, AlertTriangle } from 'lucide-react';

interface SmartWritingAssistantProps {
  biography: string;
  onInsertText: (text: string) => void;
}

const SmartWritingAssistant: React.FC<SmartWritingAssistantProps> = ({ 
  biography, 
  onInsertText 
}) => {
  const [expandedSections, setExpandedSections] = useState<string[]>(['essentials']);

  const toggleSection = (section: string) => {
    setExpandedSections(prev => 
      prev.includes(section) 
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  const getProgress = () => {
    const checks = [
      biography.toLowerCase().includes('my name') || biography.toLowerCase().includes('i am') || biography.toLowerCase().includes("i'm"),
      biography.toLowerCase().includes('family') || biography.toLowerCase().includes('wife') || biography.toLowerCase().includes('husband') || biography.toLowerCase().includes('child'),
      biography.toLowerCase().includes('work') || biography.toLowerCase().includes('job') || biography.toLowerCase().includes('career'),
      biography.toLowerCase().includes('home') || biography.toLowerCase().includes('live in') || biography.toLowerCase().includes('city'),
      biography.toLowerCase().includes('enjoy') || biography.toLowerCase().includes('love') || biography.toLowerCase().includes('hobby') || biography.toLowerCase().includes('passion'),
    ];
    return Math.round((checks.filter(Boolean).length / checks.length) * 100);
  };

  const sections = [
    {
      id: 'essentials',
      title: 'Essential Information',
      icon: User,
      color: 'blue',
      prompts: [
        "My name is [your name] and I'm [age] years old.",
        "I live in [city/area] with my [family situation].",
        "I work as a [job title] at [company/industry].",
        "The most important people in my life are [names and relationships]."
      ]
    },
    {
      id: 'relationships',
      title: 'Important Relationships',
      icon: Heart,
      color: 'pink',
      prompts: [
        "My family includes [list family members and their names].",
        "My closest friends are [names] who mean a lot to me because [reasons].",
        "I have pets named [pet names] who [describe their personality].",
        "The person who influences me most is [name] because [reason]."
      ]
    },
    {
      id: 'spaces',
      title: 'Personal Spaces & Places',
      icon: MapPin,
      color: 'green',
      prompts: [
        "My favorite room in my house is my [room] because [reason].",
        "I feel most peaceful when I'm [location/setting].",
        "My favorite place to visit is [location] because [what you love about it].",
        "When I need to think, I go to [location or space]."
      ]
    },
    {
      id: 'goals',
      title: 'Goals & Dreams',
      icon: Target,
      color: 'purple',
      prompts: [
        "My biggest goal right now is [specific goal] because [motivation].",
        "In the next year, I want to [specific aspiration].",
        "Something I'm really proud of accomplishing is [achievement].",
        "My dream is to [long-term vision] someday."
      ]
    },
    {
      id: 'challenges',
      title: 'Current Challenges',
      icon: AlertTriangle,
      color: 'orange',
      prompts: [
        "Right now I'm dealing with [current challenge] which makes me feel [emotion].",
        "When I'm stressed, I usually [coping method] to feel better.",
        "Something that always worries me is [fear or concern].",
        "The hardest part of my day is usually [specific situation]."
      ]
    }
  ];

  const getColorClasses = (color: string) => {
    const colors = {
      blue: 'border-blue-200 bg-blue-50',
      pink: 'border-pink-200 bg-pink-50',
      green: 'border-green-200 bg-green-50',
      purple: 'border-purple-200 bg-purple-50',
      orange: 'border-orange-200 bg-orange-50'
    };
    return colors[color as keyof typeof colors] || colors.blue;
  };

  return (
    <Card className="bg-white/80 backdrop-blur-lg border-white/20 h-[600px] flex flex-col">
      <CardHeader className="flex-shrink-0">
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center">
            <Lightbulb className="h-5 w-5 mr-2 text-primary" />
            Smart Writing Assistant
          </div>
          <Badge variant="secondary" className="bg-primary/10 text-primary">
            {getProgress()}% Complete
          </Badge>
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Click any prompt below to add it to your biography and customize it with your details.
        </p>
      </CardHeader>
      <CardContent className="space-y-4 flex-1 overflow-y-auto">
        {sections.map((section) => {
          const Icon = section.icon;
          const isExpanded = expandedSections.includes(section.id);
          
          return (
            <Collapsible key={section.id} open={isExpanded} onOpenChange={() => toggleSection(section.id)}>
              <CollapsibleTrigger asChild>
                <Button 
                  variant="ghost" 
                  className={`w-full justify-between p-3 h-auto ${getColorClasses(section.color)}`}
                >
                  <div className="flex items-center">
                    <Icon className="h-4 w-4 mr-2" />
                    <span className="font-medium">{section.title}</span>
                  </div>
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </Button>
              </CollapsibleTrigger>
              
              <CollapsibleContent className="mt-2">
                <div className="space-y-2 pl-6">
                  {section.prompts.map((prompt, index) => (
                    <Button
                      key={index}
                      variant="outline"
                      size="sm"
                      className="justify-start text-left h-auto p-3 w-full whitespace-normal"
                      onClick={() => onInsertText(prompt + "\n\n")}
                    >
                      <span className="text-sm">{prompt}</span>
                    </Button>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          );
        })}
        
        <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200">
          <div className="flex items-start space-x-3">
            <Lightbulb className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-900 mb-2">Writing Tips</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Use specific names of people, places, and things</li>
                <li>• Include emotions and feelings, not just facts</li>
                <li>• Mention what makes you unique and special</li>
                <li>• Add details about your daily routines and preferences</li>
              </ul>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SmartWritingAssistant;